import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppContext } from '../contexts/Storage';
import { useAuth } from '../hooks/useAuth';
import { Loading } from '../components/utils';

const DemoLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { token, user, setUser, setToken } = useAppContext();
  const { login } = useAuth();
  const [status, setStatus] = useState('Initializing...');
  const [hasAttempted, setHasAttempted] = useState(false);

  useEffect(() => {
    const performDemoLogin = async () => {
      if (hasAttempted) return;
      setHasAttempted(true);

      const demoEmail = process.env.REACT_APP_DEMO_EMAIL || 'sales@balancio.in';
      const demoPassword = process.env.REACT_APP_DEMO_PASSWORD || 'Demo@1234';

      try {
        if (token || user) {
          setStatus('Logging out current session...');
          setToken?.(null);
          setUser?.(null);
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        sessionStorage.setItem('is_demo_login', 'true');

        setStatus('Signing into demo account...');
        const result = await login({
          email: demoEmail,
          password: demoPassword,
        });

        if (result.success) {
          setStatus('Redirecting to dashboard...');
          sessionStorage.setItem('redirectAfterLogin', '/dashboard');
        } else {
          setStatus('Demo login failed. Please try again.');
          toast.error('Demo login failed: ' + (result.error || 'Unknown error'));
          setTimeout(() => navigate('/login'), 2000);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
        setStatus('Error: ' + errorMessage);
        toast.error('Demo login error: ' + errorMessage);
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    performDemoLogin();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-light p-8 rounded-lg shadow-md text-center">
        <div className="flex items-center justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
            <svg className="w-10 h-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
        </div>
        <h2 className="text-2xl font-bold text-dark mb-2">Demo Account</h2>
        <p className="text-grey_dark text-sm mb-8">Setting up your demo experience...</p>

        <div className="flex flex-col items-center justify-center gap-3 mb-8">
          <span className="text-dark text-sm">{status}</span>
          <Loading width="24px" height="24px" className="mx-2 mb-4" />
          
        </div>

        <div className="p-4 rounded-lg border border-warning bg-yellow-50 text-left">
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-xs leading-relaxed text-dark_b">
              This is a demo account where you can test the app's features. Please be aware that any data entered here is public.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoLoginPage;
